import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiToastMessageConfig } from "@miplatform/types";

export interface ICommunityInspectionAuthority {
  id: string;
  communityId: string;
  inspectionAuthorityId: string;
  inspectionAuthorityName: string;
  modifiedOn: string;
}

export interface ICommunityInspectionAuthorityTemplate {
  id: string;
  communityInspectionAuthorityId: string;
  workTemplateBaseId: string;
  workTemplateBaseName: string;
  modifiedOn: string;
}

export const useCommunityInspectionAuthorities = () => {
  const [communityInspectionAuthorities, setCommunityInspectionAuthorities] =
    useState<ICommunityInspectionAuthority[]>([]);
  const [templates, setTemplates] = useState<
    ICommunityInspectionAuthorityTemplate[]
  >([]);

  const stateManager = useComponentState();

  const getCommunityInspectionAuthorities = (communityId: string) => {
    return stateManager.run(async () => {
      const response = await homeService.communityInspectionAuthorities.getAll({
        filters: [{ communityId: { eq: { type: "guid", value: communityId } } }],
        sort: "inspectionAuthorityName",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setCommunityInspectionAuthorities(response.data);
      }
      return response;
    });
  };

  const addCommunityInspectionAuthority = async ({
    communityId,
    inspectionAuthorityId,
  }: {
    communityId: string;
    inspectionAuthorityId: string;
  }) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: { text: "Inspection authority added successfully." },
      failure: { text: "Failed to add inspection authority. Please try again." },
    };
    return stateManager.run(async () => {
      const response = await homeService.communityInspectionAuthorities.create({
        data: { communityId, inspectionAuthorityId },
        toastMessageConfig,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setCommunityInspectionAuthorities((prev) => [...prev, response.data]);
      }
      return response;
    });
  };

  const removeCommunityInspectionAuthority = async (id: string) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: { text: "Inspection authority removed successfully." },
      failure: { text: "Failed to remove inspection authority. Please try again." },
    };
    const authority = communityInspectionAuthorities.find((a) => a.id === id);
    const response = await stateManager.run(() =>
      homeService.communityInspectionAuthorities.delete({
        id,
        data: { modifiedOn: authority?.modifiedOn },
        toastMessageConfig,
      })
    );
    if (response?.status === HTTP_STATUSES.OK) {
      setCommunityInspectionAuthorities((prev) => prev.filter((a) => a.id !== id));
      setTemplates((prev) => prev.filter((t) => t.communityInspectionAuthorityId !== id));
    }
    return response;
  };

  const getTemplates = async (communityInspectionAuthorityId: string) => {
    const response = await stateManager.run(() =>
      homeService.communityInspectionAuthorities.templates.getAll({
        filters: [
          {
            communityInspectionAuthorityId: {
              eq: { type: "guid", value: communityInspectionAuthorityId },
            },
          },
        ],
      })
    );
    if (response?.status === HTTP_STATUSES.OK) {
      setTemplates((prev) => {
        const others = prev.filter(
          (t) => t.communityInspectionAuthorityId !== communityInspectionAuthorityId
        );
        return [...others, ...response.data];
      });
    }
    return response;
  };

  const addTemplate = async ({
    communityInspectionAuthorityId,
    workTemplateBaseId,
  }: {
    communityInspectionAuthorityId: string;
    workTemplateBaseId: string;
  }) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: { text: "Inspection type added successfully." },
      failure: { text: "Failed to add inspection type. Please try again." },
    };
    return stateManager.run(async () => {
      const response =
        await homeService.communityInspectionAuthorities.templates.create({
          data: { communityInspectionAuthorityId, workTemplateBaseId },
          toastMessageConfig,
        });
      if (response.status === HTTP_STATUSES.OK) {
        setTemplates((prev) => [...prev, response.data]);
      }
      return response;
    });
  };

  const removeTemplate = async (id: string) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: { text: "Inspection type removed successfully." },
      failure: { text: "Failed to remove inspection type. Please try again." },
    };
    const template = templates.find((t) => t.id === id);
    const response = await stateManager.run(() =>
      homeService.communityInspectionAuthorities.templates.delete({
        id,
        data: { modifiedOn: template?.modifiedOn },
        toastMessageConfig,
      })
    );
    if (response?.status === HTTP_STATUSES.OK) {
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    }
    return response;
  };

  return {
    communityInspectionAuthorities,
    templates,
    isLoading: stateManager.isBusy(),
    getCommunityInspectionAuthorities,
    addCommunityInspectionAuthority,
    removeCommunityInspectionAuthority,
    getTemplates,
    addTemplate,
    removeTemplate,
  };
};
